import { useState, useEffect, memo, useCallback } from 'react'
import { useSettings, useToast } from '../context'

export default memo(function SettingsModal({ isOpen, onClose }) {
  const { settings, setSetting, resetToDefaults } = useSettings()
  const { showToast } = useToast()
  const [showConfirm, setShowConfirm] = useState(false)
  const [localSettings, setLocalSettings] = useState(settings)

  useEffect(() => {
    if (isOpen) setLocalSettings(settings)
  }, [settings, isOpen])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  const handleSave = useCallback(() => {
    Object.entries(localSettings).forEach(([key, value]) => {
      setSetting(key, value)
    })
    showToast('Settings saved', 'success')
    onClose()
  }, [localSettings, setSetting, showToast, onClose])

  const confirmReset = useCallback(() => {
    resetToDefaults()
    showToast('Settings reset to defaults', 'success')
    setShowConfirm(false)
    onClose()
  }, [resetToDefaults, showToast, onClose])

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="w-full max-w-[420px] max-h-[85vh] overflow-hidden rounded-xl border animate-slide-up"
        style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-lg)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            Settings
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-[var(--color-hover)]"
            style={{ color: 'var(--text-muted)' }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto p-4 space-y-5" style={{ maxHeight: 'calc(85vh - 130px)' }}>
          {/* Theme */}
          <SettingGroup label="Theme">
            <select
              value={localSettings.theme}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, theme: e.target.value }))}
              className="input-field"
            >
              <option value="terminal">Terminal (Default)</option>
              <option value="solarized">Solarized Dark</option>
              <option value="dracula">Dracula</option>
              <option value="monokai">Monokai</option>
              <option value="amoled">AMOLED Black</option>
            </select>
          </SettingGroup>

          {/* Font Size */}
          <SettingGroup label={`Font Size: ${localSettings.fontSize}px`}>
            <input
              type="range"
              min="11"
              max="18"
              value={localSettings.fontSize}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ backgroundColor: 'var(--bg-tertiary)', accentColor: 'var(--color-accent)' }}
            />
            <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
              <span>11px</span>
              <span>18px</span>
            </div>
          </SettingGroup>

          {/* Time Format */}
          <SettingGroup label="Time Format">
            <div className="flex gap-2">
              <RadioButton
                checked={localSettings.timeFormat === '12hr'}
                onChange={() => setLocalSettings(prev => ({ ...prev, timeFormat: '12hr' }))}
                label="12-Hour"
              />
              <RadioButton
                checked={localSettings.timeFormat === '24hr'}
                onChange={() => setLocalSettings(prev => ({ ...prev, timeFormat: '24hr' }))}
                label="24-Hour"
              />
            </div>
          </SettingGroup>

          {/* Update Interval */}
          <SettingGroup label={`Auto-refresh: ${localSettings.updateInterval / 1000}s`}>
            <input
              type="range"
              min="2000"
              max="10000"
              step="1000"
              value={localSettings.updateInterval}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, updateInterval: parseInt(e.target.value) }))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ backgroundColor: 'var(--bg-tertiary)', accentColor: 'var(--color-accent)' }}
            />
            <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
              <span>2s</span>
              <span>10s</span>
            </div>
          </SettingGroup>

          {/* Visual Effects */}
          <SettingGroup label="Effects">
            <div className="space-y-2">
              <ToggleOption
                checked={localSettings.scanLines}
                onChange={(checked) => setLocalSettings(prev => ({ ...prev, scanLines: checked }))}
                label="Scan Lines"
                description="Retro CRT effect"
              />
              <ToggleOption
                checked={localSettings.glowEffect}
                onChange={(checked) => setLocalSettings(prev => ({ ...prev, glowEffect: checked }))}
                label="Glow Effect"
                description="Text glow on logs"
              />
              <ToggleOption
                checked={localSettings.liveUpdates}
                onChange={(checked) => setLocalSettings(prev => ({ ...prev, liveUpdates: checked }))}
                label="Live Updates"
                description="Auto-fetch new logs"
              />
            </div>
          </SettingGroup>
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-2 p-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={() => setShowConfirm(true)}
            className="btn btn-ghost text-xs"
          >
            Reset
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-ghost">
              Cancel
            </button>
            <button onClick={handleSave} className="btn btn-primary">
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      {showConfirm && (
        <ConfirmDialog
          title="Reset Settings"
          message="Reset all settings to their default values?"
          onConfirm={confirmReset}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  )
})

const SettingGroup = memo(function SettingGroup({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
        {label}
      </label>
      {children}
    </div>
  )
})

const RadioButton = memo(function RadioButton({ checked, onChange, label }) {
  return (
    <button
      onClick={onChange}
      className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg border transition-all ${
        checked ? 'border-[var(--color-accent)]' : 'border-transparent hover:bg-[var(--color-hover)]'
      }`}
      style={{
        backgroundColor: checked ? 'var(--color-active)' : 'var(--bg-tertiary)',
        color: checked ? 'var(--color-accent)' : 'var(--text-secondary)'
      }}
    >
      {label}
    </button>
  )
})

const ToggleOption = memo(function ToggleOption({ checked, onChange, label, description }) {
  return (
    <label className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-[var(--color-hover)] transition-colors">
      <div>
        <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{label}</div>
        {description && (
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{description}</div>
        )}
      </div>
      <div 
        className={`w-9 h-5 rounded-full p-0.5 transition-colors ${checked ? '' : ''}`}
        style={{ backgroundColor: checked ? 'var(--color-accent)' : 'var(--bg-tertiary)' }}
        onClick={(e) => { e.preventDefault(); onChange(!checked) }}
      >
        <div 
          className={`w-4 h-4 rounded-full shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
          style={{ backgroundColor: 'white' }}
        />
      </div>
    </label>
  )
})

const ConfirmDialog = memo(function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div 
      className="fixed inset-0 z-[101] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div 
        className="w-full max-w-[320px] rounded-xl border p-5 animate-slide-up"
        style={{ backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-lg)' }}
      >
        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          {title}
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
          {message}
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="btn btn-ghost text-xs">
            Cancel
          </button>
          <button 
            onClick={onConfirm} 
            className="btn text-xs"
            style={{ backgroundColor: 'var(--color-error)', color: 'white' }}
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  )
})
